/**
 * Niche Reports Redux Slice
 * Manages state for niche text report generation
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';

export type NicheStatus = 'pending' | 'completed' | 'error' | null;

export interface NicheReportEntry {
    niche_keyword: string;
    report_text: string | null;
    niche_status: NicheStatus;
}

export interface NicheReportsState {
    reports: Record<string, NicheReportEntry>;
    initialLoading: boolean;
    error: string | null;
}

const initialState: NicheReportsState = {
    reports: {},
    initialLoading: false,
    error: null,
};

const nicheReportsSlice = createSlice({
    name: 'nicheReports',
    initialState,
    reducers: {
        setInitialLoading: (state, action: PayloadAction<boolean>) => {
            state.initialLoading = action.payload;
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        setReports: (state, action: PayloadAction<NicheReportEntry[]>) => {
            action.payload.forEach((entry) => {
                state.reports[entry.niche_keyword] = entry;
            });
        },

        setReportEntry: (state, action: PayloadAction<NicheReportEntry>) => {
            state.reports[action.payload.niche_keyword] = action.payload;
        },

        clearReports: (state) => {
            state.reports = {};
            state.initialLoading = false;
            state.error = null;
        },
    },
});

export const {
    setInitialLoading,
    setError,
    setReports,
    setReportEntry,
    clearReports,
} = nicheReportsSlice.actions;

export const selectNicheReports = (state: RootState) => state.nicheReports.reports;
export const selectNicheReportEntry = (keyword: string) => (state: RootState) =>
    state.nicheReports.reports[keyword] ?? null;
export const selectReportsInitialLoading = (state: RootState) => state.nicheReports.initialLoading;

export default nicheReportsSlice.reducer;
